import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight, FlaskConical, Plus, Wand2, X } from "lucide-react";
import { RegelnAnwenden } from "./RegelnAnwenden";
import { api, CATEGORIES, formatDate, type Account, type Condition, type Folder, type Rule } from "../api";
import { Select } from "./Select";
import { bestaetigen, fehlerMelden, fehlerText, toast } from "./verwaltung";

const FIELDS: { value: string; label: string }[] = [
  { value: "from", label: "Absender-Adresse" },
  { value: "from_domain", label: "Absender-Domain" },
  { value: "to", label: "Empfänger (An/Kopie)" },
  { value: "subject", label: "Betreff" },
  { value: "body", label: "Text der Mail" },
  { value: "list_id", label: "Verteiler-Kennung" },
  { value: "has_attachment", label: "Anhang" },
  { value: "folder", label: "Ordner" },
];

const ACTION_TYPES: { value: string; label: string }[] = [
  { value: "move", label: "Verschieben nach" },
  { value: "copy", label: "Kopieren nach" },
  { value: "setCategory", label: "Kategorie setzen" },
  { value: "setPriority", label: "Dringlichkeit setzen" },
  { value: "flag", label: "Als wichtig markieren" },
  { value: "markRead", label: "Als gelesen markieren" },
  { value: "markSpam", label: "Als unerwünscht behandeln" },
  { value: "draftReply", label: "Antwort vorschreiben" },
  { value: "skipLlm", label: "Nicht ans Sprachmodell geben" },
];

const OPS: { value: string; label: string }[] = [
  { value: "contains", label: "enthält" },
  { value: "equals", label: "ist genau" },
  { value: "startsWith", label: "beginnt mit" },
  { value: "endsWith", label: "endet auf" },
  { value: "in", label: "ist eines von" },
  { value: "regex", label: "passt auf Muster (Regex)" },
];

const KATEGORIE_LABEL = (c: string) => c.charAt(0).toUpperCase() + c.slice(1);

const emptyRule = (): Rule => ({
  name: "",
  enabled: true,
  priority: 100,
  match: { all: [{ field: "from_domain", op: "equals", value: "" }] },
  actions: [{ type: "move", folder: "" }],
  stopProcessing: true,
});

function wertText(v: Condition["value"]): string {
  return Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "ja" : "nein") : v;
}

function describe(rule: Rule): string {
  const conds = [...(rule.match.all ?? []), ...(rule.match.any ?? [])];
  const first = conds[0];
  const condText = !first
    ? "keine Bedingung"
    : first.field === "has_attachment"
      ? (first.value === false ? "ohne Anhang" : "mit Anhang")
      : `${FIELDS.find((f) => f.value === first.field)?.label ?? first.field} ${
          OPS.find((o) => o.value === first.op)?.label ?? first.op
        } „${wertText(first.value)}"`;
  const extra = conds.length > 1 ? ` und ${conds.length - 1} weitere` : "";
  const acts = rule.actions
    .map((a) =>
      a.type === "move" ? `→ ${String(a.folder)}`
      : a.type === "copy" ? `Kopie → ${String(a.folder)}`
      : a.type === "setCategory" ? `Kategorie ${KATEGORIE_LABEL(String(a.category))}`
      : a.type === "setPriority" ? `Dringlichkeit ${String(a.priority)}`
      : (ACTION_TYPES.find((t) => t.value === a.type)?.label ?? a.type).toLowerCase())
    .join(", ");
  return `${condText}${extra} · ${acts || "keine Aktion"}`;
}

/** Was fehlt, damit die Regel gespeichert werden kann — als lesbare Sätze. */
function pruefeRegel(rule: Rule): string[] {
  const fehler: string[] = [];
  if (!rule.name.trim()) fehler.push("Die Regel braucht einen Namen.");
  const conds = [...(rule.match.all ?? []), ...(rule.match.any ?? []), ...(rule.match.none ?? [])];
  if (conds.length === 0) fehler.push("Mindestens eine Bedingung ist nötig — sonst träfe die Regel jede Mail.");
  for (const c of rule.match.all ?? []) {
    if (c.op === "isTrue") continue;
    const leer = Array.isArray(c.value) ? c.value.filter(Boolean).length === 0 : String(c.value).trim() === "";
    if (leer) { fehler.push(`Bei „${FIELDS.find((f) => f.value === c.field)?.label ?? c.field}" fehlt der Wert.`); continue; }
    if (c.op === "regex") {
      try { new RegExp(String(c.value)); } catch { fehler.push(`Das Muster „${String(c.value)}" ist kein gültiger regulärer Ausdruck.`); }
    }
  }
  if (rule.actions.length === 0) fehler.push("Mindestens eine Aktion ist nötig.");
  for (const a of rule.actions) {
    if ((a.type === "move" || a.type === "copy") && !String(a.folder ?? "").trim()) fehler.push("Bitte einen Zielordner wählen.");
    if (a.type === "setCategory" && !a.category) fehler.push("Bitte eine Kategorie wählen.");
    if (a.type === "setPriority" && !a.priority) fehler.push("Bitte eine Dringlichkeit wählen.");
  }
  return [...new Set(fehler)];
}

/** Vor dem Senden aufräumen: leere Listeneinträge weg, Leerraum ab. */
function bereinigt(rule: Rule): Rule {
  const all = (rule.match.all ?? []).map((c) => ({
    ...c,
    value: Array.isArray(c.value) ? c.value.map((v) => v.trim()).filter(Boolean) : typeof c.value === "string" ? c.value.trim() : c.value,
  }));
  return { ...rule, name: rule.name.trim(), priority: Math.round(rule.priority) || 0, match: { ...rule.match, all } };
}

interface Props {
  folders: Folder[];
  /** Nach dem Anlegen eines Ordners oder einem Anwenden-Lauf die Ordnerliste neu laden. */
  onFoldersChanged?: () => void;
}

type Probe = { tested: number; matched: number; hits: { id: number; subject: string; from: string; date?: string }[] };

export function RulesPage({ folders, onFoldersChanged }: Props) {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [test, setTest] = useState<Probe | null>(null);
  const [testet, setTestet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [anwenden, setAnwenden] = useState<{ id: number; name: string } | "alle" | null>(null);

  const load = useCallback(async () => {
    try {
      setRules(await api.rules());
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(fehlerText(e));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const schliessen = () => { setOpenId(null); setEditing(null); setTest(null); };

  const startEdit = (rule: Rule) => {
    setOpenId(rule.id ?? "new");
    setEditing(structuredClone(rule));
    setTest(null);
  };

  const save = async () => {
    if (!editing || pruefeRegel(editing).length) return;
    setBusy(true);
    try {
      const r = bereinigt(editing);
      if (r.id) await api.updateRule(r.id, r);
      else await api.createRule(r);
      toast.erfolg(r.id ? `Regel „${r.name}" gespeichert.` : `Regel „${r.name}" angelegt. Sie gilt ab sofort für neue Mails.`);
      await load();
      schliessen();
    } catch (err) {
      fehlerMelden(err, "Regel nicht gespeichert");
    } finally {
      setBusy(false);
    }
  };

  const probelauf = async () => {
    if (!editing) return;
    setTestet(true);
    try {
      setTest(await api.testRule(bereinigt(editing)));
    } catch (err) {
      fehlerMelden(err, "Probelauf fehlgeschlagen");
    } finally {
      setTestet(false);
    }
  };

  const loeschen = async (rule: Rule) => {
    if (!rule.id) return;
    const ja = await bestaetigen({
      titel: `Regel „${rule.name}" löschen?`,
      text: "Neue Mails, auf die sie gepasst hätte, ordnet danach wieder der Agent ein. Bereits verschobene Mails bleiben, wo sie sind.",
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (!ja) return;
    try {
      await api.deleteRule(rule.id);
      toast.erfolg(`Regel „${rule.name}" gelöscht.`);
      schliessen();
      await load();
    } catch (err) {
      fehlerMelden(err, "Löschen fehlgeschlagen");
    }
  };

  const conditions = editing?.match.all ?? [];
  const setConditions = (next: Condition[]) =>
    setEditing((r) => (r ? { ...r, match: { ...r.match, all: next } } : r));

  const editorProps = editing ? {
    rule: editing,
    folders,
    onFoldersChanged,
    conditions,
    busy,
    testet,
    test,
    onChange: setEditing,
    onConditions: setConditions,
    onTest: probelauf,
    onSave: save,
    onCancel: schliessen,
  } : null;

  const liste = rules ?? [];

  return (
    <section className="pane wide" data-active="true">
      <header className="pane-head">
        <span className="pane-title">Regeln</span>
        <div className="spacer" />
        <button className="btn" onClick={() => setAnwenden("alle")} disabled={!liste.some((r) => r.enabled)}
          title="Alle aktiven Regeln auf Mails anwenden, die schon im Postfach liegen">
          <Wand2 aria-hidden="true" /> <span className="v-schmal-weg">Auf vorhandene Mails anwenden</span><span className="only-narrow">Anwenden</span>
        </button>
        <button className="btn primary" onClick={() => startEdit(emptyRule())} disabled={openId === "new"}>
          <Plus aria-hidden="true" /> Regel anlegen
        </button>
      </header>

      <div className="pane-scroll">
        <div className="page">
          <p className="lede">
            Regeln laufen vor dem Sprachmodell und entscheiden endgültig — in der Reihenfolge ihrer Nummer, kleinste
            zuerst. Was eine Regel abfängt, kostet nichts und verhält sich jeden Tag gleich. Alles andere
            übernimmt der Agent.
          </p>

          {openId === "new" && editorProps && (
            <div className="v-panel">
              <div className="v-panel-rumpf">
                <h3 style={{ margin: "0 0 12px", fontSize: 15 }}>Neue Regel</h3>
                <RuleEditor {...editorProps} />
              </div>
            </div>
          )}

          {ladeFehler && !rules ? (
            <div className="v-leer" data-art="fehler">
              <strong>Die Regeln ließen sich nicht laden</strong>
              <span>{ladeFehler}</span>
              <button className="btn" onClick={() => void load()}>Erneut versuchen</button>
            </div>
          ) : !rules ? (
            <div className="v-laden" role="status">Lade Regeln …</div>
          ) : liste.length === 0 && openId !== "new" ? (
            <div className="v-leer">
              <strong>Noch keine Regeln</strong>
              <span>
                Am schnellsten entsteht eine Regel aus einer Mail: im Leser auf „Falsch einsortiert?" gehen. Oder
                hier oben rechts eine anlegen.
              </span>
            </div>
          ) : (
            liste.map((rule) => {
              const offen = openId === rule.id;
              return (
                <div className="rule" key={rule.id} data-disabled={!rule.enabled}>
                  <div
                    className="rule-head"
                    role="button"
                    tabIndex={0}
                    aria-expanded={offen}
                    onClick={() => (offen ? schliessen() : startEdit(rule))}
                    onKeyDown={(e) => {
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (offen ? schliessen() : startEdit(rule)); }
                    }}
                  >
                    <ChevronRight className="v-aufklapp" data-offen={offen} aria-hidden="true" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="rule-name">{rule.name}</div>
                      <div className="rule-summary">{describe(rule)}</div>
                    </div>
                    {rule.hitCount ? (
                      <span className="chip v-schmal-weg" title={rule.lastHitAt ? `Zuletzt ${formatDate(rule.lastHitAt)}` : undefined}>
                        {rule.hitCount.toLocaleString("de-DE")}× gegriffen
                      </span>
                    ) : null}
                    {rule.id && rule.enabled && (
                      <button
                        className="icon-btn"
                        title="Diese Regel auf vorhandene Mails anwenden"
                        aria-label={`„${rule.name}" auf vorhandene Mails anwenden`}
                        onClick={(e) => { e.stopPropagation(); setAnwenden({ id: rule.id!, name: rule.name }); }}
                      >
                        <Wand2 aria-hidden="true" />
                      </button>
                    )}
                    <span className="v-marke" data-an={rule.enabled}>{rule.enabled ? "aktiv" : "pausiert"}</span>
                  </div>

                  {offen && editorProps && (
                    <div className="rule-body">
                      <RuleEditor {...editorProps} onDelete={() => void loeschen(rule)} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      {anwenden && (
        <RegelnAnwenden
          folders={folders}
          rule={anwenden === "alle" ? undefined : anwenden}
          onClose={() => setAnwenden(null)}
          onDone={() => { void load(); onFoldersChanged?.(); }}
        />
      )}
    </section>
  );
}

const NEU = "__neu__";

function ordnerName(f: Folder): string {
  return f.delimiter ? f.path.split(f.delimiter).join(" / ") : f.path;
}

/**
 * Zielordner wählen oder gleich anlegen. Unterordner mit Schrägstrich:
 * „Kunden/System4" — das Trennzeichen des Servers setzt die API ein.
 */
function ZielordnerWahl({ folders, value, onChange, onCreated }: {
  folders: Folder[]; value: string; onChange: (v: string) => void; onCreated?: () => void;
}) {
  const [neu, setNeu] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Bei mehreren Postfächern fragen, wo der Ordner hin soll.
  const kontoIds = useMemo(() => [...new Set(folders.map((f) => f.account_id))], [folders]);
  const [konten, setKonten] = useState<Account[]>([]);
  const [konto, setKonto] = useState<number | null>(null);
  useEffect(() => {
    if (neu === null || kontoIds.length < 2 || konten.length) return;
    api.accounts().then(setKonten).catch(() => {});
  }, [neu, kontoIds.length, konten.length]);
  const zielKonto = konto ?? kontoIds[0];

  const anlegen = async () => {
    const teile = (neu ?? "").split("/").map((t) => t.trim()).filter(Boolean);
    if (teile.length === 0 || busy) return;
    setFehler(null);
    setBusy(true);
    try {
      const { path } = await api.ordnerAnlegen(teile, zielKonto);
      onChange(path);
      setNeu(null);
      toast.erfolg(`Ordner „${teile.join(" / ")}" angelegt.`);
      onCreated?.();
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };

  const optionen = useMemo(() => {
    const o = [...folders]
      .sort((a, b) => a.path.localeCompare(b.path, "de"))
      .map((f) => ({ value: f.path, label: ordnerName(f) }));
    // Ein frisch angelegter Ordner steht noch nicht in der Liste — trotzdem zeigen.
    if (value && !o.some((x) => x.value === value)) o.unshift({ value, label: value });
    return [...o, { value: NEU, label: "+ Neuer Ordner …" }];
  }, [folders, value]);

  if (neu !== null) {
    return (
      <div className="v-reihe">
        <input
          className="input"
          autoFocus
          style={{ flex: 1, minWidth: 160 }}
          value={neu}
          placeholder="z. B. Kunden/System4"
          aria-label="Name des neuen Ordners"
          onChange={(e) => setNeu(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); void anlegen(); }
            if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setNeu(null); }
          }}
        />
        {kontoIds.length > 1 && (
          <Select
            value={String(zielKonto ?? "")}
            onChange={(v) => setKonto(Number(v))}
            options={kontoIds.map((id) => ({ value: String(id), label: konten.find((k) => k.id === id)?.email ?? `Postfach ${id}` }))}
            aria-label="In welchem Postfach"
            style={{ width: "auto", minWidth: 180 }}
          />
        )}
        <button className="btn primary" type="button" onClick={() => void anlegen()} disabled={!neu.trim() || busy}>
          {busy ? "Legt an …" : "Anlegen"}
        </button>
        <button className="btn quiet" type="button" onClick={() => { setNeu(null); setFehler(null); }}>Abbrechen</button>
        <p className="hint" style={{ width: "100%", margin: 0 }}>Mit „/" legst du Unterordner an.</p>
        {fehler && <p className="error" style={{ width: "100%", margin: 0 }}>{fehler}</p>}
      </div>
    );
  }

  return (
    <Select
      value={value}
      placeholder="Ordner wählen …"
      options={optionen}
      aria-label="Zielordner"
      onChange={(v) => (v === NEU ? setNeu("") : onChange(v))}
    />
  );
}

interface EditorProps {
  rule: Rule;
  folders: Folder[];
  onFoldersChanged?: () => void;
  conditions: Condition[];
  busy: boolean;
  testet: boolean;
  test: Probe | null;
  onChange: (fn: (r: Rule | null) => Rule | null) => void;
  onConditions: (c: Condition[]) => void;
  onTest: () => Promise<void>;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}

/** Feld- oder Vergleichswechsel: den Wert so umbauen, dass er zur Kombination passt. */
function angepasst(c: Condition, patch: Partial<Condition>): Condition {
  const n = { ...c, ...patch };
  if (n.field === "has_attachment") return { field: n.field, op: "isTrue", value: typeof n.value === "boolean" ? n.value : true };
  if (n.op === "isTrue") n.op = "contains";
  if (n.op === "in") n.value = Array.isArray(n.value) ? n.value : typeof n.value === "string" && n.value ? n.value.split(",").map((s) => s.trim()) : [];
  else n.value = Array.isArray(n.value) ? n.value.join(", ") : typeof n.value === "boolean" ? "" : n.value;
  return n;
}

function RuleEditor({ rule, folders, onFoldersChanged, conditions, busy, testet, test, onChange, onConditions, onTest, onSave, onCancel, onDelete }: EditorProps) {
  const set = (patch: Partial<Rule>) => onChange((r) => (r ? { ...r, ...patch } : r));
  const [versucht, setVersucht] = useState(false);
  const fehler = pruefeRegel(rule);
  const verborgen = (rule.match.any?.length ?? 0) + (rule.match.none?.length ?? 0);
  const setCond = (i: number, patch: Partial<Condition>) =>
    onConditions(conditions.map((x, j) => (j === i ? angepasst(x, patch) : x)));
  const setAction = (i: number, next: Rule["actions"][number]) =>
    set({ actions: rule.actions.map((x, j) => (j === i ? next : x)) });

  return (
    <form
      style={{ paddingTop: 6 }}
      onSubmit={(e) => { e.preventDefault(); setVersucht(true); if (fehler.length === 0 && !busy) onSave(); }}
    >
      <div className="field">
        <label htmlFor={`r-name-${rule.id ?? "neu"}`}>Name</label>
        <input id={`r-name-${rule.id ?? "neu"}`} className="input" value={rule.name} autoFocus={!rule.id}
          placeholder="Wofür ist diese Regel? z. B. „Hetzner-Rechnungen“" onChange={(e) => set({ name: e.target.value })} />
      </div>

      <fieldset className="v-editor-gruppe" style={{ border: 0, padding: 0, margin: "0 0 18px" }}>
        <legend className="v-gruppe-titel">Wenn alle diese Bedingungen zutreffen</legend>
        {conditions.map((c, i) => (
          <div className="cond" key={i}>
            <Select value={c.field} options={FIELDS} aria-label={`Bedingung ${i + 1}: Feld`} onChange={(v) => setCond(i, { field: v as Condition["field"] })} />
            {c.field === "has_attachment" ? (
              <>
                <Select
                  value={c.value === false ? "nein" : "ja"}
                  options={[{ value: "ja", label: "ist vorhanden" }, { value: "nein", label: "fehlt" }]}
                  aria-label={`Bedingung ${i + 1}: Anhang`}
                  onChange={(v) => setCond(i, { value: v === "ja" })}
                />
                <span className="v-platzhalter" />
              </>
            ) : (
              <>
                <Select value={c.op} options={OPS} aria-label={`Bedingung ${i + 1}: Vergleich`} onChange={(v) => setCond(i, { op: v as Condition["op"] })} />
                <input
                  className={`input ${c.op === "regex" ? "mono" : ""}`}
                  value={Array.isArray(c.value) ? c.value.join(", ") : String(c.value)}
                  aria-label={`Bedingung ${i + 1}: Wert`}
                  aria-invalid={versucht && wertText(c.value).trim() === "" ? true : undefined}
                  placeholder={c.op === "in" ? "mehrere durch Komma getrennt" : c.op === "regex" ? "^rechnung" : c.field === "from_domain" ? "hetzner.com" : "Wert"}
                  onChange={(e) => onConditions(conditions.map((x, j) =>
                    j === i ? { ...x, value: x.op === "in" ? e.target.value.split(",").map((s) => s.trimStart()) : e.target.value } : x))}
                />
              </>
            )}
            <button type="button" className="btn quiet" onClick={() => onConditions(conditions.filter((_, j) => j !== i))}
              aria-label={`Bedingung ${i + 1} entfernen`} title="Bedingung entfernen">
              <X aria-hidden="true" />
            </button>
          </div>
        ))}
        {conditions.length === 0 && <p className="hint" style={{ margin: "0 0 8px" }}>Noch keine Bedingung.</p>}
        <button type="button" className="btn quiet" onClick={() => onConditions([...conditions, { field: "subject", op: "contains", value: "" }])}>
          <Plus aria-hidden="true" /> Bedingung hinzufügen
        </button>
        {verborgen > 0 && (
          <p className="hint">
            Diese Regel hat zusätzlich {verborgen} {verborgen === 1 ? "Bedingung" : "Bedingungen"} vom Typ „eine davon" oder
            „keine davon", die hier nicht bearbeitet werden können. Sie bleiben beim Speichern erhalten.
          </p>
        )}
        <p className="hint">Groß- und Kleinschreibung spielt keine Rolle.</p>
      </fieldset>

      <fieldset className="v-editor-gruppe" style={{ border: 0, padding: 0, margin: "0 0 18px" }}>
        <legend className="v-gruppe-titel">Dann</legend>
        {rule.actions.map((a, i) => (
          <div className="cond" data-art="aktion" key={i}>
            <Select
              value={a.type}
              options={ACTION_TYPES}
              aria-label={`Aktion ${i + 1}`}
              onChange={(v) => setAction(i, v === "move" || v === "copy" ? { type: v, folder: String(a.folder ?? "") } : { type: v })}
            />

            {(a.type === "move" || a.type === "copy") ? (
              <ZielordnerWahl
                folders={folders}
                value={String(a.folder ?? "")}
                onChange={(v) => setAction(i, { ...a, folder: v })}
                onCreated={onFoldersChanged}
              />
            ) : a.type === "setCategory" ? (
              <Select
                value={String(a.category ?? "")}
                placeholder="Kategorie wählen …"
                options={CATEGORIES.map((c) => ({ value: c, label: KATEGORIE_LABEL(c) }))}
                aria-label="Kategorie"
                onChange={(v) => setAction(i, { ...a, category: v })}
              />
            ) : a.type === "setPriority" ? (
              <Select
                value={String(a.priority ?? "")}
                placeholder="Dringlichkeit wählen …"
                options={[{ value: "hoch", label: "hoch" }, { value: "mittel", label: "mittel" }, { value: "niedrig", label: "niedrig" }]}
                aria-label="Dringlichkeit"
                onChange={(v) => setAction(i, { ...a, priority: v })}
              />
            ) : a.type === "draftReply" ? (
              <input
                className="input"
                placeholder="Anweisung (optional), z. B. „auf Englisch antworten“"
                aria-label="Anweisung für den Entwurf"
                value={String(a.instruction ?? "")}
                onChange={(e) => setAction(i, { ...a, instruction: e.target.value })}
              />
            ) : (
              <span className="v-platzhalter">
                {a.type === "skipLlm" ? "Spart Kosten — sinnvoll für Systemmails." : a.type === "markSpam" ? "Landet im Ordner Unerwünscht." : ""}
              </span>
            )}

            <button type="button" className="btn quiet" onClick={() => set({ actions: rule.actions.filter((_, j) => j !== i) })}
              aria-label={`Aktion ${i + 1} entfernen`} title="Aktion entfernen">
              <X aria-hidden="true" />
            </button>
          </div>
        ))}
        <button type="button" className="btn quiet" onClick={() => set({ actions: [...rule.actions, { type: "flag" }] })}>
          <Plus aria-hidden="true" /> Aktion hinzufügen
        </button>
      </fieldset>

      <div className="v-checks">
        <label className="v-check">
          <input type="checkbox" checked={rule.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
          Aktiv
        </label>
        <label className="v-check" title="Passt diese Regel, prüft der Agent keine weiteren Regeln mehr">
          <input type="checkbox" checked={rule.stopProcessing} onChange={(e) => set({ stopProcessing: e.target.checked })} />
          Danach keine weiteren Regeln prüfen
        </label>
        <label className="v-check">
          Reihenfolge
          <input className="input v-num" type="number" step={1} style={{ width: 80, height: 30 }} value={rule.priority}
            onChange={(e) => set({ priority: e.target.value === "" ? 0 : Number(e.target.value) })}
            aria-describedby="r-reihenfolge-hilfe" />
        </label>
        <span id="r-reihenfolge-hilfe" className="hint" style={{ margin: 0 }}>kleinere Zahl = früher</span>
      </div>

      {test && (
        <div className="v-probe" role="status">
          <strong>{test.matched.toLocaleString("de-DE")} von {test.tested.toLocaleString("de-DE")}</strong> zuletzt eingegangenen
          Mails würden passen.
          {test.hits.length > 0 && (
            <ul>
              {test.hits.slice(0, 6).map((h) => (
                <li key={h.id}><span>{h.from}</span><span>{h.subject || "(ohne Betreff)"}</span></li>
              ))}
              {test.hits.length > 6 && <li><span /><span className="hint" style={{ margin: 0 }}>… und {test.hits.length - 6} weitere</span></li>}
            </ul>
          )}
        </div>
      )}

      {versucht && fehler.length > 0 && (
        <div className="v-meldung" data-art="fehler" role="alert">
          <span>{fehler.join(" ")}</span>
        </div>
      )}

      <div className="v-knoepfe">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Speichert …" : rule.id ? "Speichern" : "Anlegen"}
        </button>
        <button type="button" className="btn" onClick={() => void onTest()} disabled={testet || conditions.length === 0}
          title="Zeigt, auf welche vorhandenen Mails die Regel passen würde — ohne etwas zu ändern">
          <FlaskConical aria-hidden="true" /> {testet ? "Prüft …" : "Probelauf"}
        </button>
        <button type="button" className="btn quiet" onClick={onCancel}>Abbrechen</button>
        <div className="spacer" />
        {onDelete && <button type="button" className="btn danger" onClick={onDelete}>Löschen</button>}
      </div>
    </form>
  );
}
